import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Container,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { oauthService } from '@/services/oauthService';
import { authManager } from '@/services/authManager';
import { useAppDispatch } from '@/hooks/redux';
import { loginSuccess } from '@/store/authSlice';

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');
      const provider = searchParams.get('provider');

      // Handle error from OAuth provider
      if (errorParam) {
        const errorMessages: Record<string, string> = {
          invalid_provider: 'Invalid OAuth provider',
          invalid_state: 'Invalid or expired session. Please try again.',
          oauth_denied: 'OAuth authorization was denied',
          auth_failed: 'Authentication failed. Please try again.',
          not_linked: `Your ${provider || 'OAuth'} account is not linked to any account. Please log in with email/password first, then link your ${provider || 'OAuth'} account in settings.`,
          user_not_found: 'User not found',
          server_error: 'An unexpected error occurred. Please try again.',
        };

        setError(errorMessages[errorParam] || 'An error occurred during authentication');
        setLoading(false);
        return;
      }

      // No code means something went wrong
      if (!code) {
        setError('No authorization code received');
        setLoading(false);
        return;
      }

      try {
        if (cancelled) return;

        // Exchange the code for tokens and user data
        const response = await oauthService.exchangeCode(code);
        const tokens = response;
        const user = response.user;

        // Update auth manager with new session
        await authManager.updateSession(
          tokens.accessToken,
          tokens.expiresIn,
          tokens.refreshToken,
          user
        );

        // Update Redux state
        dispatch(loginSuccess({
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresIn: tokens.expiresIn,
          user,
        }));

        // Redirect to the original destination (if preserved) or dashboard
        const returnTo = window.sessionStorage.getItem('oauth_return_to');
        window.sessionStorage.removeItem('oauth_return_to');
        navigate(returnTo || '/dashboard', { replace: true });
      } catch (err) {
        if (cancelled) return;
        console.error('OAuth callback error:', err);
        setError('Failed to complete authentication. Please try again.');
        setLoading(false);
      }
    };

    handleCallback();
    return () => { cancelled = true; };
  }, [searchParams, navigate, dispatch]);

  if (loading && !error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Card sx={{ width: '100%', textAlign: 'center' }}>
            <CardContent sx={{ p: 4 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6">
                Completing authentication...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please wait while we sign you in.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/login', { replace: true })}
                startIcon={<ArrowBackIcon />}
              >
                Back to Login
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default OAuthCallback;
